import type { PageContext } from './page-context'

export const PAGE_VIEW = 'page_view' as const
export const BOOKING_MODAL_OPENED = 'booking_modal_opened' as const
export const BOOKING_STEP_COMPLETED = 'booking_step_completed' as const
export const BOOKING_SUBMITTED = 'booking_submitted' as const
export const BOOKING_FAILED = 'booking_failed' as const
export const PHONE_CLICKED = 'phone_clicked' as const
export const EMAIL_CLICKED = 'email_clicked' as const
export const ATTRIBUTION_CAPTURED = 'attribution_captured' as const
export const MICROSOFT_ENQUIRY_SUBMITTED = 'cg_enquiry_submitted' as const

export type BookingConversionIntent = 'assessment' | 'online_quote' | 'callback_lead'
/**
 * Fired the moment someone opens a destructive-test or explainer clip, from
 * the click on the poster facade. Deliberately independent of GTM's built-in
 * YouTube trigger: that trigger reports watch depth but depends on the
 * YouTube iframe API loading, which an ad blocker or a slow network can
 * prevent. This event is our own, fires before the iframe exists, and is the
 * one we count intent on. Watch-time milestones stay GTM's job.
 */
export const VIDEO_PLAY = 'video_play' as const

export type EventName =
  | typeof PAGE_VIEW
  | typeof BOOKING_MODAL_OPENED
  | typeof BOOKING_STEP_COMPLETED
  | typeof BOOKING_SUBMITTED
  | typeof BOOKING_FAILED
  | typeof PHONE_CLICKED
  | typeof EMAIL_CLICKED
  | typeof ATTRIBUTION_CAPTURED
  | typeof VIDEO_PLAY

export type CtaLocation =
  | 'header'
  | 'hero'
  | 'sticky_mobile'
  | 'final_cta'
  | 'footer'
  | 'inline'
  | 'book_page'
  | 'unknown'

export type PageViewPayload = PageContext & {
  entry_path?: string
  last_path?: string
}

export type BookingModalOpenedPayload = PageContext & {
  cta_location: CtaLocation
  rep_code?: string
  lead_source?: string
}

export type BookingStepCompletedPayload = {
  step_index: number
  step_name: string
  cta_location: CtaLocation
}

export type BookingSubmittedPayload = {
  reference_code: string
  intent: string
  submission_id: string
}

/** Where on the page a clip was opened. Distinct from `CtaLocation`, which
 *  describes conversion controls, not media. */
export type VideoLocation =
  | 'lp_hero'
  | 'lp_proof_band'
  | 'lp_sean_explainer'
  | 'sean_explainer'
  | 'inline'

export type VideoPlayPayload = PageContext & {
  /** YouTube id, so a clip can be followed across every page it appears on. */
  video_id: string
  /** Human-readable caption, for reading GA4 reports without a lookup table. */
  video_title: string
  video_location: VideoLocation
}

export type AttributionCapturedPayload = {
  rep_code?: string
  lead_source?: string
}

export type BookingFailedPayload = {
  error_code: string
  cta_location: CtaLocation
}

export type PhoneClickedPayload = {
  location: CtaLocation
  label: string
}

export type EmailClickedPayload = {
  location: CtaLocation
  label: string
}

export type AnalyticsEvent =
  | { name: typeof PAGE_VIEW; params: PageViewPayload }
  | { name: typeof BOOKING_MODAL_OPENED; params: BookingModalOpenedPayload }
  | { name: typeof BOOKING_STEP_COMPLETED; params: BookingStepCompletedPayload }
  | { name: typeof BOOKING_SUBMITTED; params: BookingSubmittedPayload }
  | { name: typeof BOOKING_FAILED; params: BookingFailedPayload }
  | { name: typeof PHONE_CLICKED; params: PhoneClickedPayload }
  | { name: typeof EMAIL_CLICKED; params: EmailClickedPayload }
  | { name: typeof ATTRIBUTION_CAPTURED; params: AttributionCapturedPayload }
  | { name: typeof VIDEO_PLAY; params: VideoPlayPayload }
